import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
} from "@mui/material";
import { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import PalletIcon from "@mui/icons-material/Pallet";
import DeleteIcon from "@mui/icons-material/Close";

type PaletteData = {
  id: string;
  moduleType: string;
  moduleOrders: string[];
};

type Props = {
  palettes: {
    A?: PaletteData;
    A2?: PaletteData;
    B?: PaletteData;
    C?: PaletteData;
    Q?: PaletteData;
  };
  onEditSave?: (updatedOrders: string[]) => void;
};

const paletteColors: Record<string, string> = {
  A: "green",
  A2: "blue",
  B: "orange",
  C: "red",
  Q: "purple",
};

function Palettes({ palettes, onEditSave }: Props) {
  const paletteKeys = ["A", "A2", "B", "C", "Q"] as const;

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [tempOrders, setTempOrders] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showPalleteDialog, setShowPalleteDialog] = useState(false);
  const [removedOrders, setRemovedOrders] = useState<string[]>([]);

  const startEdit = (key: string, orders: string[]) => {
    setEditingKey(key);
    setTempOrders([...orders]);
  };

  const removeOrder = (index: number) => {
    const removed = tempOrders[index];
    setRemovedOrders((prev) => [...prev, removed]);
    setTempOrders((prev) => prev.filter((_, i) => i !== index));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setTempOrders([]);
    setRemovedOrders([]);
  };

  const saveEdit = () => {
    if (removedOrders.length > 0) {
      setShowConfirmDialog(true);
    } else {
      commitEdit();
    }
  };

  const commitEdit = async () => {
    if (editingKey && onEditSave) {
      await onEditSave(removedOrders);
    }
    cancelEdit();
    setRemovedOrders([]);
    setShowConfirmDialog(false);
  };

  const totalModules = paletteKeys.reduce(
    (sum, k) => sum + (palettes[k]?.moduleOrders.length ?? 0),
    0
  );

  return (
    <>
      <Box display="grid" gridTemplateColumns="repeat(5, 1fr)" gap={2} mt={2}>
        {paletteKeys.map((key) => {
          const data = palettes[key];
          const isEditing = editingKey === key;

          const count = data
            ? isEditing
              ? tempOrders.length
              : data.moduleOrders.length
            : 0;

          return (
            <Card
              key={key}
              sx={{
                p: 2,
                minHeight: 180,
                maxHeight: 425,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <Chip
                size="small"
                icon={<PalletIcon fontSize="small" />}
                label={`${count} module${count === 1 ? "" : "s"}`}
                variant="outlined"
              />
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  color={paletteColors[key]}
                >
                  {`Palette ${key}`}
                </Typography>

                <div>
                  {data && (
                    <Tooltip title="Close Packing List">
                      <IconButton
                        size="small"
                        onClick={() => setShowPalleteDialog(true)}
                      >
                        <PalletIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {data && !isEditing && (
                    <Tooltip title="Edit Palette">
                      <IconButton
                        size="small"
                        onClick={() => startEdit(key, data.moduleOrders)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </Box>

              {data ? (
                <Box mt={1}>
                  <Typography variant="body1" fontWeight="bold">
                    {data.id}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ wordBreak: "break-word" }}
                  >
                    {data.moduleType}
                  </Typography>

                  <Typography variant="body2" fontWeight="bold" mt={1} mb={0.5}>
                    Module Orders:
                  </Typography>

                  <Box
                    sx={{
                      mt: 1,
                      maxHeight: 200,
                      overflowY: "auto",
                      pr: 1,
                    }}
                  >
                    {(isEditing ? tempOrders : data.moduleOrders).map(
                      (order, i) => (
                        <Box
                          key={order}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="body2" fontFamily="monospace">
                            {order}
                          </Typography>

                          {isEditing && (
                            <IconButton
                              size="small"
                              onClick={() => removeOrder(i)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )
                    )}
                  </Box>

                  {isEditing && (
                    <Stack direction="row" spacing={1} mt={2}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={saveEdit}
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={cancelEdit}
                        color="inherit"
                      >
                        Cancel
                      </Button>
                    </Stack>
                  )}
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  mt={2}
                  color="text.secondary"
                  fontStyle="italic"
                >
                  No articles
                </Typography>
              )}
            </Card>
          );
        })}
      </Box>
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Deleting items: <strong>{removedOrders.join(", ")}</strong> from
            Palette <strong>{editingKey}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowConfirmDialog(false)}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              commitEdit();
            }}
            color="error"
            variant="contained"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={showPalleteDialog}
        onClose={() => setShowPalleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Close Packing List</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Error: Windows Packing List Protocol not found
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowPalleteDialog(false)}
            color="error"
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Palettes;
